/**
 * Client-side pivot for query results (#575), aligned with server/utils/determinaciones-pivot.js
 */

export type PivotMethodView = {
  skuMetodo: number;
  descLMetodo?: string | null;
  metodoOrden?: number | null;
  hasSecondary?: boolean;
};

function parseDateMs(value: unknown): number {
  if (value == null || value === '') return NaN;
  const t = Date.parse(String(value));
  return Number.isNaN(t) ? NaN : t;
}

function compareDeterminacionRows(
  a: Record<string, unknown>,
  b: Record<string, unknown>
): number {
  const va = parseDateMs(a['fechaValidacionDt']);
  const vb = parseDateMs(b['fechaValidacionDt']);
  if (!Number.isNaN(va) && !Number.isNaN(vb) && va !== vb) return va - vb;
  if (!Number.isNaN(va) && Number.isNaN(vb)) return 1;
  if (Number.isNaN(va) && !Number.isNaN(vb)) return -1;

  const ma = parseDateMs(a['fechaMetodoDt']);
  const mb = parseDateMs(b['fechaMetodoDt']);
  if (!Number.isNaN(ma) && !Number.isNaN(mb) && ma !== mb) return ma - mb;
  if (!Number.isNaN(ma) && Number.isNaN(mb)) return 1;
  if (Number.isNaN(ma) && !Number.isNaN(mb)) return -1;

  const ra = parseDateMs(a['fechaResultado']);
  const rb = parseDateMs(b['fechaResultado']);
  if (!Number.isNaN(ra) && !Number.isNaN(rb) && ra !== rb) return ra - rb;

  const sa = String(a['resultado'] ?? '');
  const sb = String(b['resultado'] ?? '');
  return sa.localeCompare(sb, 'es', { sensitivity: 'base' });
}

function pickBetterDeterminacionRow(
  a: Record<string, unknown>,
  b: Record<string, unknown>
): Record<string, unknown> {
  return compareDeterminacionRows(a, b) >= 0 ? a : b;
}

function pivotFieldKey(skuMetodo: number): string {
  return `param_${skuMetodo}`;
}

function pivotFieldKey2(skuMetodo: number): string {
  return `param_${skuMetodo}_2`;
}

function sortMethods(methods: PivotMethodView[]): PivotMethodView[] {
  const copy = [...methods];
  copy.sort((a, b) => {
    const ao = a.metodoOrden;
    const bo = b.metodoOrden;
    const aNull = ao == null || Number.isNaN(Number(ao));
    const bNull = bo == null || Number.isNaN(Number(bo));
    if (!aNull && !bNull && ao !== bo) return Number(ao) - Number(bo);
    if (aNull !== bNull) return aNull ? 1 : -1;
    const al = (a.descLMetodo || '').toString();
    const bl = (b.descLMetodo || '').toString();
    if (al !== bl) return al.localeCompare(bl, 'es', { sensitivity: 'base' });
    return (a.skuMetodo || 0) - (b.skuMetodo || 0);
  });
  return copy;
}

/**
 * One pivoted row per CODPETICION for the current flat rows (one result page).
 */
export function buildPivotViewRows(
  flatRows: Record<string, unknown>[],
  methodDescriptors: PivotMethodView[]
): Record<string, unknown>[] {
  const methods = sortMethods(methodDescriptors);
  const byCodPeticion = new Map<string | number, Map<number, Record<string, unknown>>>();

  for (const row of flatRows) {
    if (row == null || row['skuMetodo'] == null) continue;
    const cod = row['codpeticion'];
    if (cod == null || (typeof cod === 'string' && cod.trim() === '')) continue;
    if (!byCodPeticion.has(cod as string | number)) {
      byCodPeticion.set(cod as string | number, new Map());
    }
    const byMethod = byCodPeticion.get(cod as string | number)!;
    const sku = Number(row['skuMetodo']);
    const prev = byMethod.get(sku);
    byMethod.set(sku, prev ? pickBetterDeterminacionRow(prev, row) : row);
  }

  const rows: Record<string, unknown>[] = [];
  for (const [codpeticion, byMethod] of byCodPeticion.entries()) {
    const anyRow = byMethod.values().next().value as Record<string, unknown> | undefined;
    const out: Record<string, unknown> = {
      codpeticion,
      numPeticion: anyRow ? anyRow['numPeticion'] : undefined,
      fechaPeticionDt: anyRow ? anyRow['fechaPeticionDt'] : undefined,
    };
    for (const m of methods) {
      const chosen = byMethod.get(m.skuMetodo) as Record<string, unknown> | undefined;
      const k1 = pivotFieldKey(m.skuMetodo);
      const v1 = chosen?.['resultado'];
      out[k1] = v1 == null || v1 === '' ? '' : v1;
      if (m.hasSecondary) {
        const k2 = pivotFieldKey2(m.skuMetodo);
        const v2 = chosen?.['resultado2'];
        out[k2] = v2 == null || v2 === '' ? '' : v2;
      }
    }
    rows.push(out);
  }

  rows.sort((a, b) => {
    const da = a['fechaPeticionDt'] ? Date.parse(String(a['fechaPeticionDt'])) : NaN;
    const db = b['fechaPeticionDt'] ? Date.parse(String(b['fechaPeticionDt'])) : NaN;
    if (!Number.isNaN(da) && !Number.isNaN(db) && da !== db) return db - da;
    const n = String(a['numPeticion'] || '').localeCompare(String(b['numPeticion'] || ''), 'es', {
      numeric: true,
    });
    if (n !== 0) return n;
    return String(a['codpeticion'] || '').localeCompare(String(b['codpeticion'] || ''), 'es', {
      numeric: true,
    });
  });

  return rows;
}

export function countPivotViewColumns(methods: PivotMethodView[]): number {
  let n = 2;
  for (const m of methods) {
    n += m.hasSecondary ? 2 : 1;
  }
  return n;
}

export function buildPivotDynamicColumns(
  methods: PivotMethodView[],
  translateSecondarySuffix: string
): { field: string; header: string }[] {
  const cols: { field: string; header: string }[] = [];
  for (const m of sortMethods(methods)) {
    const label = (m.descLMetodo || `SKU ${m.skuMetodo}`).toString();
    cols.push({ field: pivotFieldKey(m.skuMetodo), header: label });
    if (m.hasSecondary) {
      cols.push({
        field: pivotFieldKey2(m.skuMetodo),
        header: `${label}${translateSecondarySuffix}`,
      });
    }
  }
  return cols;
}
